// =============================================================================
//  BrandLogo - logo NIS365 Auditor inline en SVG (v1 restauree).
//  Source de verite design : brand/logos/logo-horizontal.svg + icon.svg
//
//  On embarque le SVG inline (pas de <img>) pour 3 raisons :
//   1. Pas de round-trip HTTP : rendu instantane meme offline (PWA future)
//   2. Heritage de currentColor sur le wordmark si on bascule en mono
//   3. Personnalisation runtime (taille, variante) sans avoir 5 fichiers
//
//  Variantes :
//   - "horizontal" : bouclier + wordmark NIS365 + sous-titre AUDITOR (default)
//   - "icon"       : bouclier seul (favicons, badges, avatars)
//   - "mono"       : tout en couleur foreground (pied de page)
//
//  Note : la variante v2 "EU compliance" est sauvegardee dans BrandLogo.eu.tsx.
// =============================================================================

import { tokens } from '@fluentui/react-components';

type Variant = 'horizontal' | 'icon' | 'mono';

interface BrandLogoProps {
  height?: number;
  variant?: Variant;
  /** Override couleur pour le mode mono. Defaut : token brandForeground1. */
  monoColor?: string;
}

export function BrandLogo({
  height    = 40,
  variant   = 'horizontal',
  monoColor,
}: BrandLogoProps) {
  if (variant === 'icon') {
    return <IconOnly height={height} />;
  }
  if (variant === 'mono') {
    return <Horizontal height={height} mono={monoColor ?? tokens.colorBrandForeground1} />;
  }
  return <Horizontal height={height} />;
}

// ---------- variantes -------------------------------------------------------

function Horizontal({ height, mono }: { height: number; mono?: string }) {
  // viewBox 280x80 -> ratio 3.5, donc width = height * 3.5
  const width = Math.round(height * 3.5);
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 280 80"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label="NIS365 Auditor"
    >
      <defs>
        <linearGradient id="brandLogoShieldGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"   stopColor="#1F3D5C" />
          <stop offset="100%" stopColor="#2E8B96" />
        </linearGradient>
      </defs>

      {/* Bouclier */}
      <g transform="translate(8 4)">
        <path
          d="M18 12 L62 12 A4 4 0 0 1 66 16 L66 36 C66 52 54 64 40 72 C26 64 14 52 14 36 L14 16 A4 4 0 0 1 18 12 Z"
          fill={mono ?? 'url(#brandLogoShieldGrad)'}
        />
        {!mono && (
          <path
            d="M22 18 L58 18 L58 36 C58 49 48 59 40 64 C32 59 22 49 22 36 Z"
            fill="none"
            stroke="rgba(255,255,255,0.16)"
            strokeWidth="1"
          />
        )}
        <path
          d="M25 40 L36 51 L55 28"
          fill="none"
          stroke={mono ? '#FFFFFF' : '#FFFFFF'}
          strokeWidth="5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>

      {/* Wordmark */}
      <g fontFamily="Inter, 'Segoe UI', system-ui, -apple-system, sans-serif">
        <text
          x="92"
          y="46"
          fontSize="28"
          fontWeight={700}
          fill={mono ?? '#1F3D5C'}
          letterSpacing="-0.6"
        >
          NIS
          <tspan fill={mono ?? '#2E8B96'}>365</tspan>
        </text>
        <text
          x="92"
          y="62"
          fontSize="10"
          fontWeight={600}
          fill={mono ?? '#5E8AB5'}
          letterSpacing="3.2"
        >
          A U D I T O R
        </text>
      </g>
    </svg>
  );
}

function IconOnly({ height }: { height: number }) {
  return (
    <svg
      width={height}
      height={height}
      viewBox="0 0 80 80"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label="NIS365"
    >
      <defs>
        <linearGradient id="brandLogoIconGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"   stopColor="#1F3D5C" />
          <stop offset="100%" stopColor="#2E8B96" />
        </linearGradient>
      </defs>
      <g transform="translate(0 4)">
        <path
          d="M18 12 L62 12 A4 4 0 0 1 66 16 L66 36 C66 52 54 64 40 72 C26 64 14 52 14 36 L14 16 A4 4 0 0 1 18 12 Z"
          fill="url(#brandLogoIconGrad)"
        />
        <path
          d="M22 18 L58 18 L58 36 C58 49 48 59 40 64 C32 59 22 49 22 36 Z"
          fill="none"
          stroke="rgba(255,255,255,0.16)"
          strokeWidth="1"
        />
        <path
          d="M25 40 L36 51 L55 28"
          fill="none"
          stroke="#FFFFFF"
          strokeWidth="5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>
    </svg>
  );
}
